import axios from 'axios';
import { put, takeLatest, call, cps } from 'redux-saga/effects';

import { classes } from '../../BusinessLogic/Network/API/ClassesApi';
import { profile } from '../../BusinessLogic/Network/API/ProfileApi';
import * as types from '../ActionTypes/ClassesActionTypes';
import { ClassesActionCreators } from '../ActionCreators/ClassesActionCreators';

export class ClassesSaga {
    static *getClasses({ meta: { profileId, completionCallback } }) {
        try {
            const _profile = profile.create();
            const { data: response } = yield call(axios, _profile.getClasses(profileId));

            let _offsetId = 0;
            let _offsetDate = 0;

            if (response.data.length > 0) {
                _offsetId = response.data[response.data.length - 1].id;
                _offsetDate = response.data[response.data.length - 1].start;
            }

            yield put(ClassesActionCreators.getClassesSuccess(response.data, _offsetId, _offsetDate));
            yield cps(completionCallback);
        } catch (e) {
            yield put(ClassesActionCreators.getClassesFailed(e.message));
        }
    }

    static *getClassesByDate({ meta: { profileId, offsetId, offsetDate, dateFilter } }) {
        try {
            const _profile = profile.create();
            const { data: response } = yield call(
                axios,
                _profile.getClassesByDate(profileId, offsetId, offsetDate, dateFilter),
            );
            yield put(ClassesActionCreators.getClassesByDateSuccess(response.data));
        } catch (e) {
            console.log({ e });
        }
    }

    static *getClassesByDateRange({ meta: { startDate, endDate } }) {
        try {
            const _classes = classes.create();
            const { data: response } = yield call(axios, _classes.getClassesByDateRange(startDate, endDate));
            yield put(ClassesActionCreators.getClassesByDateRangeSuccess(response.data));
        } catch (error) {
            yield put(ClassesActionCreators.getClassesByDateRangeFailed(error));
        }
    }

    static *getMoreClasses({ meta: { profileId, offsetId, offsetDate } }) {
        try {
            const _profile = profile.create();

            const { data: response } = yield call(axios, _profile.getClasses(profileId, offsetId, offsetDate));

            let _offsetId = 0;
            let _offsetDate = 0;

            if (response.data.length > 0) {
                _offsetId = response.data[response.data.length - 1].id;
                _offsetDate = response.data[response.data.length - 1].start;
            }

            yield put(ClassesActionCreators.loadMoreClassesSuccess(response.data, _offsetId, _offsetDate));
        } catch (e) {
            console.log({ error: e });
        }
    }

    static *createClass({ meta: { classData, callback } }) {
        try {
            const _profile = profile.create();
            yield call(axios, _profile.createClass(classData));
            yield put(ClassesActionCreators.clearClassErrors());
            yield put(ClassesActionCreators.createClassSuccess());
            yield cps(callback);
        } catch (error) {
            if (error.response && error.response.status === 422) {
                yield put(ClassesActionCreators.createClassFailed(error.response.data.errors));
                console.log({ error });
            }
        }
    }

    static *updateClass({ meta: { classId, classData, callback } }) {
        try {
            const _profile = profile.create();
            yield call(axios, _profile.updateClass(classId, classData));
            yield put(ClassesActionCreators.clearClassErrors());
            yield put(ClassesActionCreators.updateClassSuccess());
            yield cps(callback);
        } catch (error) {
            if (error.response && error.response.status === 422) {
                yield put(ClassesActionCreators.updateClassFailed(error.response.data.errors));
            }
        }
    }

    static *updateClassSeries({ meta: { seriesId, classData, callback } }) {
        try {
            const _classes = classes.create();
            yield call(axios, _classes.updateClassSeries(seriesId, classData));
            yield put(ClassesActionCreators.clearClassErrors());
            yield put(ClassesActionCreators.updateClassSeriesSuccess());
            if (callback) yield cps(callback);
        } catch (error) {
            if (error.response && error.response.status === 422) {
                yield put(ClassesActionCreators.updateClassFailed(error.response.data.errors));
            }
        }
    }

    static *getClassBookers({ meta: { classId } }) {
        try {
            const _profile = profile.create();
            const { data: response } = yield call(axios, _profile.getClassBookers(classId));
            yield put(ClassesActionCreators.getClassBookersSuccess(response.data));
        } catch (error) {
            console.log(error);
        }
    }

    static *getClassMembers({ meta: { classId } }) {
        try {
            const _profile = profile.create();
            const { data: response } = yield call(axios, _profile.getClassMembers(classId));
            yield put(ClassesActionCreators.getClassMembersSuccess(response.data));
        } catch (error) {
            console.log(error);
        }
    }

    static *checkInUser({ meta: { classId, userId, planMemberId, callback } }) {
        try {
            const _profile = profile.create();
            yield call(axios, _profile.checkInUser(classId, userId, planMemberId));
            yield put(ClassesActionCreators.checkInUserSuccess(userId));
            if (callback) yield cps(callback);
        } catch (error) {
            console.log(error);
        }
    }

    static *checkOutUser({ meta: { classId, userId, planMemberId, callback } }) {
        try {
            const _profile = profile.create();
            yield call(axios, _profile.checkOutUser(classId, userId, planMemberId));
            yield put(ClassesActionCreators.checkOutUserSuccess(userId));
            if (callback) yield cps(callback);
        } catch (error) {
            console.log(error);
        }
    }

    static *deleteClass({ meta: { classId, callback } }) {
        try {
            const _classes = classes.create();
            yield call(axios, _classes.deleteClass(classId));
            yield put(ClassesActionCreators.clearClassErrors());
            yield put(ClassesActionCreators.deleteClassSuccess());
            if (callback) yield cps(callback);
        } catch (error) {
            console.log(error);
        }
    }

    static *deleteClassSeries({ meta: { seriesId, callback } }) {
        try {
            const _classes = classes.create();
            yield call(axios, _classes.deleteClassSeries(seriesId));
            yield put(ClassesActionCreators.clearClassErrors());
            yield put(ClassesActionCreators.deleteClassSeriesSuccess());
            if (callback) yield cps(callback);
        } catch (error) {
            console.log(error);
        }
    }

    static *bookClass({ meta: { classId } }) {
        try {
            const _classes = classes.create();
            const { data: response } = yield call(axios, _classes.bookClass(classId));
            yield put(ClassesActionCreators.bookClassSuccess(response.data));
        } catch (error) {
            yield put(ClassesActionCreators.bookClassFailed(error));
        }
    }

    static *cancelClassBooking({ meta: { classId } }) {
        try {
            const _classes = classes.create();
            const { data: response } = yield call(axios, _classes.cancelClassBooking(classId));
            yield put(ClassesActionCreators.cancelClassBookingSuccess(response.data));
        } catch (error) {
            yield put(ClassesActionCreators.cancelClassBookingFailed(error));
        }
    }
}

export function* saga() {
    yield takeLatest(types.GET_CLASSES_REQUEST, ClassesSaga.getClasses);
    yield takeLatest(types.GET_CLASSES_BY_DATE_REQUEST, ClassesSaga.getClassesByDate);
    yield takeLatest(types.LOAD_MORE_CLASSES_REQUEST, ClassesSaga.getMoreClasses);
    yield takeLatest(types.CREATE_CLASS_REQUEST, ClassesSaga.createClass);
    yield takeLatest(types.UPDATE_CLASS_REQUEST, ClassesSaga.updateClass);
    yield takeLatest(types.UPDATE_CLASS_SERIES_REQUEST, ClassesSaga.updateClassSeries);
    yield takeLatest(types.GET_CLASS_BOOKERS_REQUEST, ClassesSaga.getClassBookers);
    yield takeLatest(types.GET_CLASS_MEMBERS_REQUEST, ClassesSaga.getClassMembers);
    yield takeLatest(types.CHECK_IN_USER_REQUEST, ClassesSaga.checkInUser);
    yield takeLatest(types.CHECK_OUT_USER_REQUEST, ClassesSaga.checkOutUser);
    yield takeLatest(types.DELETE_CLASS_REQUEST, ClassesSaga.deleteClass);
    yield takeLatest(types.DELETE_CLASS_SERIES_REQUEST, ClassesSaga.deleteClassSeries);
    yield takeLatest(types.GET_CLASSES_BY_DATE_RANGE_REQUEST, ClassesSaga.getClassesByDateRange);
    yield takeLatest(types.BOOK_CLASS_REQUEST, ClassesSaga.bookClass);
    yield takeLatest(types.CANCEL_CLASS_BOOKING_REQUEST, ClassesSaga.cancelClassBooking);
}



// WEBPACK FOOTER //
// ./src/Store/Saga/ClassesSaga.js